import { useEffect, useMemo, useState } from "react"
import { Loader2, Smartphone } from "lucide-react"
import qrcode from "@/vendor/qrcode-generator"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { producaoApi, errorMessage, type Device, type PairingCode, type Technician } from "./api"
import { useProducao } from "./context"
import { Pill } from "./components"
import { confirmDialog } from "@/lib/confirm"

/**
 * G5 · Telemóveis da empresa (app de campo). Cada técnico pode ter um ou mais
 * telemóveis emparelhados; «Revogar» corta o acesso no pedido seguinte.
 */
export function useDevices() {
  const { shell } = useProducao()
  const [devices, setDevices] = useState<Device[] | null>(null)
  const reload = async () => {
    if (!shell) return
    try {
      setDevices(await producaoApi.devices(shell))
    } catch {
      setDevices([])
    }
  }
  useEffect(() => {
    void reload()
  }, [shell]) // eslint-disable-line react-hooks/exhaustive-deps
  return { devices, reload }
}

export function ago(iso: string | null): string {
  if (!iso) return "nunca"
  const min = Math.round((Date.now() - Date.parse(iso)) / 60_000)
  if (min < 2) return "agora"
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 48) return `há ${h} h`
  return `há ${Math.round(h / 24)} dias`
}

export function PhoneCell({
  tech,
  devices,
  onPair,
  onChanged,
}: {
  tech: Technician
  devices: Device[] | null
  onPair(): void
  onChanged(): void
}) {
  const { shell } = useProducao()
  const mine = (devices ?? []).filter((d) => d.technician_id === tech.id && !d.revoked_at)
  const revoke = async (d: Device) => {
    if (!shell || !(await confirmDialog(`Revogar o telemóvel ${d.model ?? ""} de ${tech.name}? Deixa de sincronizar de imediato.`, { destructive: true, confirmLabel: "Revogar" }))) return
    try {
      await producaoApi.revokeDevice(shell, d.id)
      shell.bridge.toast({ variant: "success", title: "Telemóvel revogado" })
      onChanged()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível revogar", description: errorMessage(err) })
    }
  }
  if (devices === null) return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
  return (
    <div className="space-y-1">
      {mine.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
          <Pill tone="success">{d.model || "Telemóvel"}</Pill>
          <span className="text-muted-foreground">
            v{d.app_version ?? "?"} · sincronizou {ago(d.last_seen_at)}
          </span>
          <button type="button" className="text-destructive hover:underline" onClick={() => revoke(d)}>
            Revogar
          </button>
        </div>
      ))}
      {tech.active && (
        <Button size="sm" variant="outline" className="h-7" onClick={onPair}>
          <Smartphone className="h-3.5 w-3.5" /> {mine.length ? "Emparelhar outro" : "Emparelhar telemóvel"}
        </Button>
      )}
    </div>
  )
}

/** QR + código de 6 algarismos, 10 minutos, uso único. */
export function PairDialog({ tech, onClose }: { tech: Technician; onClose(paired: boolean): void }) {
  const { shell } = useProducao()
  const [pairing, setPairing] = useState<PairingCode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [now, setNow] = useState(Date.now())

  const generate = async () => {
    if (!shell) return
    setError(null)
    setPairing(null)
    try {
      setPairing(await producaoApi.pairTechnician(shell, tech.id))
    } catch (err) {
      setError(errorMessage(err))
    }
  }
  useEffect(() => {
    void generate()
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const svg = useMemo(() => {
    if (!pairing) return null
    const qr = qrcode(0, "M")
    qr.addData(pairing.qr)
    qr.make()
    return qr.createSvgTag({ cellSize: 6, margin: 2, scalable: true })
  }, [pairing])

  const left = pairing ? Math.max(0, Math.floor((Date.parse(pairing.expires_at) - now) / 1000)) : 0
  const expired = pairing !== null && left === 0

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(true)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Emparelhar o telemóvel de {tech.name}</DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!pairing && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {pairing && (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              Na app de campo, toque em «Emparelhar este telemóvel» e aponte a câmara para o código.
            </p>
            <div
              className={expired ? "mx-auto w-56 opacity-20" : "mx-auto w-56"}
              aria-label="Código QR de emparelhamento"
              role="img"
              dangerouslySetInnerHTML={{ __html: svg ?? "" }}
            />
            <div>
              <p className="text-xs text-muted-foreground">Ou escreva o código</p>
              <p className="font-mono text-3xl font-semibold tracking-[0.3em]">{pairing.code}</p>
            </div>
            <p className={expired ? "text-sm text-destructive" : "text-xs text-muted-foreground"}>
              {expired
                ? "O código expirou."
                : `Válido mais ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")} · só serve uma vez`}
            </p>
          </div>
        )}
        <DialogFooter>
          {expired && <Button variant="outline" onClick={generate}>Gerar outro código</Button>}
          <Button onClick={() => onClose(true)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
